// Form Component to Add or Edit status category

import { useEffect, useState } from "react";
import { Button, Form, Input, Row, Col, Select, Space } from "antd";
import FormLayout, {
  FormContent,
  FormFooter,
  FormHeader,
} from "../../../../components/form/form";
import { FormRow } from "../../../../components/form/form-row";
import {
  TConfigStatus,
  TDropdownOptions,
  TProjectCategory,
  TResponseModel,
} from "../../../../types/config";
import { eResultCode } from "../../../../utils/enum";
import useFetch from "../../../../hooks/useFetch";
import useToast from "../../../../hooks/useToast";
import { ToastOpen, ToastType } from "../../../../state/toast/slice";
import {
  AddEditConfigGroup,
  AddEditProjectCategoryStatus,
  GetProjectCategoryList,
  GetSpecificConfigGroup,
  GetSpecificProjectCategory,
  GetStatusList,
} from "../../../../utils/api.constant";
import TextArea from "antd/es/input/TextArea";
import { requiredFieldValidator } from "../../../../utils/input.validator";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import CustomDropdown from "../../../../components/custom-dropdown/custom-dropdown";
import { PageSize } from "../../../../utils/constants";

type TDrawerProps = {
  groupUniqueId: string;
  id: number;
  onCloseDrawer: () => void;
  onRefreshList: () => void;
};

/**
 * Form Component to Add or Edit Config Group
 *
 * @param {TDrawerProps} props - drawer props
 * @returns {JSX.Element}
 */
export default function ProjectCategoryAndStatusForm(props: TDrawerProps) {
  const [form] = Form.useForm(); // Form instance
  const [selectedStatus, setSelectedStatus] = useState<string[]>([]);
  const [statusOptions, setStatusOptions] = useState<TDropdownOptions[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false); // Loading state
  const { post } = useFetch(); // Hook to make API calls
  const { onShowToast } = useToast(); // Hook to show toast

  // Hook to handle on load operations
  useEffect(() => {
    getStatusList();
    specificProjectCategory();
  }, []);

  // Handle on submit event
  const onFinish = async (values: TConfigStatus) => {
    try {
      const payload = {
        data: {
          ...values,
          id: props.id ? props.id : 0,
        },
      };
      setIsLoading(true);

      const response = await post(AddEditProjectCategoryStatus, payload);
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
        form.resetFields();
        props.onRefreshList();
        props.onCloseDrawer();
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusList = async () => {
    try {
      const requestPayload = {
        data: {
          pageSize: PageSize,
          currentPage: 1,
        },
      };

      const response: TResponseModel = await post(
        GetStatusList,
        requestPayload,
      );

      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        setStatusOptions(
          response.data.map((item: any) => ({
            label: item.name,
            value: item.id,
          })),
        );
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error fetching employees:", error);
    }
  };

  const specificProjectCategory = async () => {
    if (props.id) {
      try {
        const payload = {
          data: {
            id: props.id,
          },
        };
        setIsLoading(true);
        const response = await post(GetSpecificProjectCategory, payload);
        if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
          const { data } = response;
          const statusIds =
            data.categoryStatus?.map((status: { id: number }) => status.id) ||
            [];
          const formValues = {
            projectCategoryId: data.id,
            statusIds,
          };
          form.setFieldsValue(formValues);
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.centerTop,
            content: response.dataResponse.description,
          });
        }
      } catch (error) {
        console.log(error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <FormLayout>
      <Form form={form} name="register" onFinish={onFinish} layout="vertical">
        <FormHeader>
          <CloseOutlined
            style={{ cursor: "pointer", marginTop: "0.3rem", color: "white" }}
            onClick={() => {
              props.onCloseDrawer();
            }}
          />
        </FormHeader>

        <FormContent>
          {/* <Row gutter={16}>
            <Space>
              <Col span={24}>
                <h2 style={{ color: "#fff", marginTop: "10px" }}>
                  {`${props.id ? "Edit" : "Add"} a Status Configuration`}
                </h2>
              </Col>
            </Space>
          </Row> */}
          <Row gutter={16} className="formRowHeight">
            <Col span={24}>
              <Form.Item
                name="projectCategoryId"
                label="Project Category"
                rules={[
                  {
                    required: true,
                    message: "enter project category",
                  },
                ]}
              >
                <CustomDropdown
                  endPoint={GetProjectCategoryList}
                  value={form.getFieldValue("projectCategory")}
                  selectedName={form.getFieldValue("projectCategory")}
                  onChange={(selected: any) => {
                    form.setFieldValue("projectCategoryId", selected.value);
                    form.setFieldValue("projectCategory", selected.label);
                  }}
                  label=""
                  placeHolder="Select project category"
                ></CustomDropdown>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16} className="formRowHeight">
            <Col span={24}>
              <Form.Item
                name="statusIds"
                label="Status"
                rules={[
                  {
                    required: true,
                    message: "Please select at least one Status",
                  },
                ]}
              >
                <Select
                  mode="multiple"
                  placeholder="Select status"
                  style={{ width: "100%" }}
                >
                  {statusOptions.map((status) => (
                    <Select.Option key={status.value} value={status.value}>
                      {status.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </FormContent>
        <FormFooter className="formFooter">
          <Button
            type="default"
            disabled={isLoading}
            className="btnDimensions"
            onClick={props.onCloseDrawer}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            className="btnDimensions"
            onClick={form.submit}
            disabled={isLoading}
          >
            Save
          </Button>
        </FormFooter>
      </Form>
    </FormLayout>
  );
}
